import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

interface Code {
  id: string;
  personalCode: string;
  name: string | null;
  note: string | null;
  isActive: boolean;
}

interface Props {
  code: Code | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function EditCodeDialog({ code, onOpenChange, onSaved }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (code) {
      setName(code.name ?? "");
      setNote(code.note ?? "");
      setError("");
    }
  }, [code]);

  if (!code) return null;

  const handleSave = async () => {
    setError("");
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/whitelist/${code.id}`,
        { name: name || null, note: note || null },
        { withCredentials: true }
      );
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm(t("admin.whitelist.confirmHardDelete"))) return;
    setError("");
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/whitelist/${code.id}/permanent`,
        { withCredentials: true }
      );
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!code} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.whitelist.editCode")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldCode")}</Label>
            <Input value={code.personalCode} disabled className="font-mono" />
          </div>
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldNote")}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={handleHardDelete}
            disabled={submitting || deleting}
            className="sm:mr-auto"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {t("admin.whitelist.deletePermanently")}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting || deleting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={submitting || deleting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
